import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';

export type FloatingVideoMode = 'idle' | 'docked' | 'mini' | 'expanded';

export interface ActiveTrainingVideo {
  itemId: string;
  embedSrc: string;
  title: string;
  categoryTitle: string;
  driveUrl?: string;
}

interface FloatingTrainingVideoContextValue {
  active: ActiveTrainingVideo | null;
  mode: FloatingVideoMode;
  dockMountEl: HTMLElement | null;
  setDockMountEl: (el: HTMLElement | null) => void;
  openVideo: (video: ActiveTrainingVideo) => void;
  minimize: () => void;
  expand: () => void;
  dock: () => void;
  close: () => void;
  isPlayingAway: boolean;
}

const FloatingTrainingVideoContext = createContext<FloatingTrainingVideoContextValue | null>(null);

export const FloatingTrainingVideoProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const [active, setActive] = useState<ActiveTrainingVideo | null>(null);
  const [mode, setMode] = useState<FloatingVideoMode>('idle');
  const [dockMountEl, setDockMountEl] = useState<HTMLElement | null>(null);

  const openVideo = useCallback((video: ActiveTrainingVideo) => {
    setActive(video);
    setMode('docked');
  }, []);

  const minimize = useCallback(() => {
    setMode((prev) => (prev === 'idle' ? prev : 'mini'));
  }, []);

  const expand = useCallback(() => {
    setMode((prev) => (prev === 'idle' ? prev : 'expanded'));
  }, []);

  const dock = useCallback(() => {
    setMode((prev) => (prev === 'idle' ? prev : 'docked'));
  }, []);

  const close = useCallback(() => {
    setActive(null);
    setMode('idle');
  }, []);

  useEffect(() => {
    if (mode === 'docked' && active && !dockMountEl) {
      setMode('mini');
    }
  }, [mode, active, dockMountEl]);

  const isPlayingAway = Boolean(active && mode !== 'docked');

  const value = useMemo(
    () => ({
      active,
      mode,
      dockMountEl,
      setDockMountEl,
      openVideo,
      minimize,
      expand,
      dock,
      close,
      isPlayingAway,
    }),
    [active, mode, dockMountEl, openVideo, minimize, expand, dock, close, isPlayingAway],
  );

  return (
    <FloatingTrainingVideoContext.Provider value={value}>
      {children}
    </FloatingTrainingVideoContext.Provider>
  );
};

export function useFloatingTrainingVideo() {
  const ctx = useContext(FloatingTrainingVideoContext);
  if (!ctx) {
    throw new Error('useFloatingTrainingVideo must be used within FloatingTrainingVideoProvider');
  }
  return ctx;
}
